import { useMemo } from 'react';
import type {
  GetWorkspacesListQuery,
  GetWorkspacesQuery,
  Workspace,
} from '@frameio/graphql-client/generated';
import { useGetInvitedProjectsQuery } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { DEFAULT_PROJECT_SORT_BY } from '@frameio/shared/resources/constants';
import { appComponentsStorage } from '@frameio/shared/storage';
import { mapSortByToProjectOrderInputs } from '@frameio/shared/utils';
import { INVITED_TO_ID } from '../../constants';
import TEXT from './generated/TEXT/useCreateWorkspacesList';
import { useWorkspacesProjectFilterAsInput } from './useWorkspacesProjectFilterAsInput';

TEXT.DEFINE({
  invitedToName: 'Invited to',
});
/**
 * Hook used to get a list of workspaces including those by invitation.
 * @returns a list of all workspaces.
 */
export const useCreateWorkspacesList = (
  workspacesData: GetWorkspacesQuery | GetWorkspacesListQuery | undefined
) => {
  const createInvitedToSection = (
    projects: Array<{
      id: string;
    }>
  ) =>
    ({
      id: INVITED_TO_ID,
      __typename: 'Workspace',
      name: TEXT.invitedToName,
      projectCount: projects.length,
      projects: {
        pageInfo: {},
        totalCount: projects.length,
        nodes: projects,
      },
    }) as Workspace;
  const accountId = useActiveAccountId();
  const projectStatusFilterInput = useWorkspacesProjectFilterAsInput();
  const projectOrder = mapSortByToProjectOrderInputs(
    appComponentsStorage.get(`frame-io--project-sort-by`) ||
      DEFAULT_PROJECT_SORT_BY
  );
  const { data: invitedProjectsData } = useGetInvitedProjectsQuery({
    variables: {
      accountId,
      filter: { projectStatus: projectStatusFilterInput },
      orders: [projectOrder],
    },
    skip: !accountId,
  });
  const workspacesList = useMemo(() => {
    if (!workspacesData) return [];
    const fullAccessProjects =
      invitedProjectsData?.account?.fullAccessProjects?.nodes || [];
    const partialAccessProjects =
      invitedProjectsData?.account?.partialAccessProjects?.nodes || [];
    const invitedProjects = [...fullAccessProjects, ...partialAccessProjects];
    const workspaceNodes =
      (workspacesData as GetWorkspacesQuery)?.account?.workspaces?.nodes || [];
    if (invitedProjects.length) {
      const invitedToSection = createInvitedToSection(invitedProjects);
      return workspaceNodes.concat(invitedToSection);
    }
    return workspaceNodes;
  }, [workspacesData, invitedProjectsData]);
  return workspacesList;
};
